/**
 * Deal Flow Redux Reducer
 *
 * Manages state for admin rating system and interest stats viewing.
 * Follows existing admin reducer patterns.
 */

import {
  ADMIN_SUBMIT_RATING_REQUEST,
  ADMIN_SUBMIT_RATING_SUCCESS,
  ADMIN_SUBMIT_RATING_FAIL,
  ADMIN_GET_RATINGS_REQUEST,
  ADMIN_GET_RATINGS_SUCCESS,
  ADMIN_GET_RATINGS_FAIL,
  ADMIN_GET_INTEREST_STATS_REQUEST,
  ADMIN_GET_INTEREST_STATS_SUCCESS,
  ADMIN_GET_INTEREST_STATS_FAIL,
  ADMIN_RECALCULATE_STATS_REQUEST,
  ADMIN_RECALCULATE_STATS_SUCCESS,
  ADMIN_RECALCULATE_STATS_FAIL,
  ADMIN_DEAL_FLOW_CLEAR_ERROR,
  ADMIN_DEAL_FLOW_RESET,
} from "../constants/dealFlowConstants";

const initialState = {
  // Rating submission state
  submitting: false,
  submitError: null,

  // Campaign ratings (keyed by campaignId)
  ratings: {},
  ratingsLoading: false,
  ratingsError: null,

  // Interest stats (keyed by campaignId)
  interestStats: {},
  statsLoading: false,
  statsError: null,

  // Recalculation state
  recalculating: false,
  recalculateError: null,
};

export const dealFlowReducer = (state = initialState, action) => {
  switch (action.type) {
    // Rating Submission
    case ADMIN_SUBMIT_RATING_REQUEST:
      return {
        ...state,
        submitting: true,
        submitError: null,
      };

    case ADMIN_SUBMIT_RATING_SUCCESS:
      // Update the my_rating in the ratings cache if we have it
      const { campaignId, rating, campaignAverage } = action.payload;
      const existingRatings = state.ratings[campaignId] || {};

      return {
        ...state,
        submitting: false,
        submitError: null,
        ratings: {
          ...state.ratings,
          [campaignId]: {
            ...existingRatings,
            my_rating: rating,
            // Update average if provided
            ...(campaignAverage !== undefined && { average_rating: campaignAverage }),
          },
        },
      };

    case ADMIN_SUBMIT_RATING_FAIL:
      return {
        ...state,
        submitting: false,
        submitError: action.payload,
      };

    // Get Ratings
    case ADMIN_GET_RATINGS_REQUEST:
      return {
        ...state,
        ratingsLoading: true,
        ratingsError: null,
      };

    case ADMIN_GET_RATINGS_SUCCESS:
      return {
        ...state,
        ratingsLoading: false,
        ratingsError: null,
        ratings: {
          ...state.ratings,
          [action.payload.campaignId]: action.payload.ratings,
        },
      };

    case ADMIN_GET_RATINGS_FAIL:
      return {
        ...state,
        ratingsLoading: false,
        ratingsError: action.payload,
      };

    // Get Interest Stats
    case ADMIN_GET_INTEREST_STATS_REQUEST:
      return {
        ...state,
        statsLoading: true,
        statsError: null,
      };

    case ADMIN_GET_INTEREST_STATS_SUCCESS:
      return {
        ...state,
        statsLoading: false,
        statsError: null,
        interestStats: {
          ...state.interestStats,
          [action.payload.campaignId]: action.payload.stats,
        },
      };

    case ADMIN_GET_INTEREST_STATS_FAIL:
      return {
        ...state,
        statsLoading: false,
        statsError: action.payload,
      };

    // Recalculate Stats
    case ADMIN_RECALCULATE_STATS_REQUEST:
      return {
        ...state,
        recalculating: true,
        recalculateError: null,
      };

    case ADMIN_RECALCULATE_STATS_SUCCESS:
      return {
        ...state,
        recalculating: false,
        recalculateError: null,
        // Stats will be refreshed by subsequent getCampaignRatingsAdmin and getInterestStats calls
      };

    case ADMIN_RECALCULATE_STATS_FAIL:
      return {
        ...state,
        recalculating: false,
        recalculateError: action.payload,
      };

    // Clear Errors
    case ADMIN_DEAL_FLOW_CLEAR_ERROR:
      return {
        ...state,
        submitError: null,
        ratingsError: null,
        statsError: null,
        recalculateError: null,
      };

    // Reset State
    case ADMIN_DEAL_FLOW_RESET:
      return initialState;

    default:
      return state;
  }
};

export default dealFlowReducer;
